/**
 * Matrix service helpers: timeouts, VoIP fallbacks, listener safety, error checks.
 */

import { ClientEvent } from 'matrix-js-sdk';
import { CallEventHandler } from 'matrix-js-sdk/lib/webrtc/callEventHandler.js';
import { GroupCallEventHandler } from 'matrix-js-sdk/lib/webrtc/groupCallEventHandler.js';
import { supportsMatrixCall } from 'matrix-js-sdk/lib/webrtc/call.js';
import { addBreadcrumb, captureChatException } from 'shared/sentry';
import { isAbortError } from '../../utils/isAbortError.js';

/**
 * Matrix orchestrator singleton (`Matrix` in `./index.js`).
 * @typedef {Object} MatrixService
 * @property {import('matrix-js-sdk').MatrixClient|null} matrixClient
 * @property {number} initGeneration
 * @property {Array<() => void>} [_clientListenerDisposers]
 * @property {Promise<*>|null} [refreshPromise]
 * @property {Promise<*>|null} [initPromise]
 * @property {Promise<boolean>|null} [syncRestartPromise]
 * @property {Promise<*>|null} [_tokenExpiryHandling]
 * @property {number} [recoveryAttempts]
 * @property {number} [maxRecoveryAttempts]
 * @property {number} [lastSyncRestartAt]
 * @property {number} [lastHardFailureAt]
 * @property {number} [lastTokenToastAt]
 * @property {boolean} [_timelineReady]
 * @property {(reason: string, err?: *) => Promise<void>} [handleTokenExpiry]
 * @property {(opts?: *) => Promise<boolean>} [requestSyncRecovery]
 * @property {(opts?: *) => Promise<boolean>} [restartSync]
 * @property {(ready: boolean) => void} [emitTimelineReady]
 * @property {(payload: *) => void} [_emitStateChange]
 * @property {(opts?: *) => void} [reset]
 * @property {(reason?: string) => void} [_invokeAuthFailure]
 * @property {(matrixUserInfo?: *, opts?: *) => Promise<boolean>} [reinitialize]
 * @property {(opts?: *) => void} [schedulePostSyncTimelineHydration]
 */

/**
 * True when the orchestrator still owns this client instance (not replaced by a newer init).
 * @param {{ matrixClient?: import('matrix-js-sdk').MatrixClient|null }} service
 * @param {import('matrix-js-sdk').MatrixClient|null|undefined} clientRef
 * @returns {boolean}
 */
export function isSameMatrixClient(service, clientRef) {
  return service?.matrixClient === clientRef;
}

/**
 * @template T
 * @param {Promise<T>} promise
 * @param {number} ms
 * @param {string} [label]
 * @returns {Promise<T>}
 */
export function withTimeout(promise, ms, label = 'timeout') {
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error(label)), ms);
    }),
  ]);
}

export function delay(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

// matrix-js-sdk WebRTC call teardown can crash if `client.callEventHandler`
// is missing and it tries to read `.calls`.
const createCallEventHandlerFallback = () => {
  return {
    calls: new Map(),
    _calls: new Map(),
    __fallback: true,
  };
};

/**
 * After stopClient(), matrix-js-sdk clears callEventHandler but startClient() does not recreate it.
 * Start listeners once initial sync is ready (SDK's one-shot Sync listener may already be gone).
 * @param {import('matrix-js-sdk').MatrixClient} matrixClient
 */
function startRestoredVoipHandlers(matrixClient) {
  if (!matrixClient || matrixClient.__jhVoipHandlersStarted) return;
  if (!supportsMatrixCall?.()) return;

  const run = () => {
    if (matrixClient.__jhVoipHandlersStarted) return true;
    if (!matrixClient.isInitialSyncComplete?.()) return false;
    try {
      matrixClient.callEventHandler?.start?.();
      const gStart = matrixClient.groupCallEventHandler?.start?.();
      if (gStart && typeof gStart.then === 'function') {
        void gStart.catch(() => {});
      }
      matrixClient.__jhVoipHandlersStarted = true;
    } catch (e) {
      if (!isAbortError(e)) {
        captureChatException(e, {
          tags: { matrix: 'voip' },
          extra: { phase: 'voip-handlers-start' },
        });
      }
      matrixClient.__jhVoipHandlersStarted = true;
    }
    return true;
  };

  if (run()) return;

  const kick = () => {
    if (run()) {
      try {
        matrixClient.removeListener?.(ClientEvent.Sync, kick);
      } catch (_) {}
    }
  };
  matrixClient.on?.(ClientEvent.Sync, kick);
}

/**
 * Ensure `client.callEventHandler` (and group handler) exist after sync restarts.
 * Without this, `call.js` can throw reading `client.callEventHandler.calls` during hangup.
 *
 * @returns {{ ready: boolean, usedFallback: boolean, restored: boolean }}
 */
export function ensureCallEventHandler(matrixClient) {
  if (!matrixClient) return { ready: false, usedFallback: false, restored: false };

  const hasRealHandlers =
    matrixClient.callEventHandler &&
    typeof matrixClient.callEventHandler.start === 'function' &&
    !matrixClient.callEventHandler.__fallback &&
    matrixClient.groupCallEventHandler &&
    typeof matrixClient.groupCallEventHandler.start === 'function';

  if (hasRealHandlers) {
    return { ready: true, usedFallback: false, restored: false };
  }

  if (!matrixClient.disableVoip && supportsMatrixCall()) {
    try {
      matrixClient.__jhVoipHandlersStarted = false;
      matrixClient.callEventHandler = new CallEventHandler(matrixClient);
      matrixClient.groupCallEventHandler = new GroupCallEventHandler(matrixClient);
      matrixClient.canSupportVoip = true;
      if (matrixClient.clientRunning) {
        startRestoredVoipHandlers(matrixClient);
      }
      return { ready: true, usedFallback: false, restored: true };
    } catch (e) {
      if (!isAbortError(e)) {
        captureChatException(e, {
          tags: { matrix: 'voip' },
          extra: { phase: 'restore-call-event-handler' },
        });
      }
    }
  }

  try {
    if (!matrixClient.callEventHandler) {
      matrixClient.callEventHandler = createCallEventHandlerFallback();
    }
    if (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production') {
      addBreadcrumb({
        category: 'matrix.voip',
        message: 'ensureCallEventHandler: using minimal stub; 1:1 calls need VoIP restore',
        level: 'warning',
      });
    }
    return { ready: true, usedFallback: true, restored: false };
  } catch (_) {
    return { ready: false, usedFallback: false, restored: false };
  }
}

const getCallCollection = (matrixClient) => {
  const handler = matrixClient?.callEventHandler;
  if (!handler) return null;
  return handler.calls || handler._calls || null;
};

const collectCalls = (callsContainer) => {
  if (!callsContainer) return [];
  try {
    if (typeof callsContainer.values === 'function') {
      return Array.from(callsContainer.values() ?? []);
    }
  } catch (_) {}
  return [];
};

const isLiveCall = (call) => {
  if (!call) return false;
  try {
    if (typeof call.callHasEnded === 'function') return !call.callHasEnded();
  } catch (_) {}

  const state = String(call?.state || '').toLowerCase();
  if (!state) return true;
  return state !== 'ended' && state !== 'failed';
};

export function hasInProgressMatrixCall(matrixClient) {
  const callsContainer = getCallCollection(matrixClient);
  const calls = collectCalls(callsContainer);
  return calls.some(isLiveCall);
}

/**
 * Wrap event listener bodies so one crash doesn't break future handlers.
 * @param {Function} fn
 * @param {string} label
 * @returns {Function}
 */
export function safeListener(fn, label = 'listener') {
  return (...args) => {
    try {
      return fn(...args);
    } catch (e) {
      if (!isAbortError(e)) {
        captureChatException(e, {
          tags: { matrix: 'listener' },
          extra: { label },
        });
      }
      return undefined;
    }
  };
}

export { isAbortError };

/**
 * Whether the sync state is considered "healthy".
 * @param {string|null|undefined} state
 * @returns {boolean}
 */
export function isHealthySyncState(state) {
  return state === 'SYNCING' || state === 'PREPARED' || state === 'CATCHUP';
}
